/**
 * Reload once when a chunk fails to load.
 *
 * A deploy replaces the hashed chunk files, so a page loaded before it asks for names that no
 * longer exist. Reloading fetches the current shell and, with it, the current names. One reload
 * per window: a chunk that is missing for any other reason reaches the error boundary as before.
 * The failure itself is left to propagate, so the error card and Sentry see the real cause.
 */

const RELOAD_MARK = 'stale-chunk-reload'
// Longer than any browser's request timeout, so a slow failure cannot outlast the guard and reload
// again; a second failure inside the window reaches the error card and stays there.
const RELOAD_WINDOW_MS = 10 * 60_000

/** Returns the function that stops listening. */
export function reloadOnStaleChunk(reload: () => void = () => window.location.reload()): () => void {
  const onChunkFailure = () => {
    const lastReload = Number(sessionStorage.getItem(RELOAD_MARK) ?? 0)
    if (Date.now() - lastReload < RELOAD_WINDOW_MS) return
    sessionStorage.setItem(RELOAD_MARK, String(Date.now()))
    reload()
  }
  window.addEventListener('vite:preloadError', onChunkFailure)
  return () => window.removeEventListener('vite:preloadError', onChunkFailure)
}
